import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import {
  BsChevronDown,
  BsChevronRight,
  BsArrowReturnLeft,
  BsExclamationTriangleFill,
  BsFolder,
  BsShieldLock,
  BsCheck,
  BsSearch,
} from 'react-icons/bs';
import { uniquenessWarning } from '../uniquenessWarning';
import { FixedSizeList, VariableSizeList } from 'react-window';
import {
  hasFindingEntries,
  topFindingEntity,
  maskingInventoryRowStatus,
} from 'Secure/Utils';
import './InventoryFieldsAccordion.css';

// Rows per expanded group rendered without virtualization. Beyond this a group switches to a
// react-window list (see GROUP_VIRTUALIZE_THRESHOLD). Mirrors the design mock's cap.
const CAP = 60;
// Only virtualize a group once it would render more than this many rows — small groups stay as
// plain DOM (cheaper, keeps the markup simple). The spec: virtualize only groups that genuinely
// render hundreds.
const GROUP_VIRTUALIZE_THRESHOLD = 100;
const VIRTUAL_ROW_HEIGHT = 44;
const VIRTUAL_LIST_MAX_HEIGHT = 480;
const NO_PREFIX = '(no '; // "(no findings)" / "(no algorithm)" placeholder buckets sort last

// ---- group-list windowing (for many-table databases, e.g. PeopleSoft with 1K-10K tables) ----
// Above this many groups, the GROUP list itself is virtualized (react-window VariableSizeList) so
// only on-screen table headers mount. Below it, groups render directly (cheaper, and lets a single
// wide table use its own inner row-virtualization without nesting two virtual lists).
const GROUP_WINDOW_THRESHOLD = 40;
const GROUP_HEADER_H = 49; // collapsed group = just the header bar
const GROUP_COLHEADER_H = 33; // the Field/Findings/… column-header row inside an open group
const GROUP_TIP_H = 38; // the "showing N of M" cap note
const GROUP_LIST_MAX_H = 560; // viewport height of the windowed group list
const GROUP_ROW_GAP = 9; // .inv-acc margin-bottom

/** Split a "schema.table" key the same way the save side does: first "." splits; unqualified → ''. */
function splitTableKey(tableKey) {
  const parts = String(tableKey).split('.');
  if (parts.length > 1) {
    return { schema: parts[0], table: parts.slice(1).join('.') };
  }
  return { schema: '', table: String(tableKey) };
}

/** Flatten selectedFields (table → field → descriptor) into a stable row list.
 *  Exported for the file-source inventory view (FileInventoryGroups). */
export function flattenRows(selectedFields) {
  const rows = [];
  for (const tableKey of Object.keys(selectedFields || {})) {
    const { schema, table } = splitTableKey(tableKey);
    const fields = selectedFields[tableKey] || {};
    for (const field of Object.keys(fields)) {
      const fd = fields[field] || {};
      const algorithm =
        fd.algorithm != null && String(fd.algorithm).trim() !== '' ? String(fd.algorithm).trim() : '';
      rows.push({
        tableKey,
        field,
        schema,
        table,
        dataType: fd.dataType,
        findings: fd.findings,
        checked: Boolean(fd.checked),
        algorithm,
        recommendedAlgorithm:
          fd.recommendedAlgorithm != null ? String(fd.recommendedAlgorithm).trim() : '',
        // Flat masking params (dateFormat / nameOrder) and their recommendation, carried so the
        // per-row and per-group "use recommended" writers apply params alongside the algorithm.
        params: fd.params && typeof fd.params === 'object' ? fd.params : {},
        recommendedParams:
          fd.recommendedParams && typeof fd.recommendedParams === 'object' ? fd.recommendedParams : {},
        // Profile-sourced PK / UNIQUE membership (null when none) — the uniqueness advisory's
        // evidence; the name heuristic is only the fallback when this is absent.
        keyConstraints: Array.isArray(fd.keyConstraints) ? fd.keyConstraints : null,
        // Target comparison overlay (targetComparison.applyTargetGaps): the selected mask target
        // lacks this column ('column') or its whole table ('table'). Rendered greyed-out, cannot
        // be selected or given an algorithm, and never persisted into the derived inventory.
        targetMissing: fd.targetMissing === true,
        targetMissingReason: fd.targetMissing === true ? fd.targetMissingReason || 'column' : null,
        saved: Boolean(fd.saved),
        topEntity: topFindingEntity(fd.findings) || '',
        status: maskingInventoryRowStatus({
          saved: Boolean(fd.saved),
          algorithm,
          recommendedAlgorithm: fd.recommendedAlgorithm,
        }),
      });
    }
  }
  return rows;
}

/**
 * Distinct PRIMARY (top) findings across rows, for the findings filter dropdown. Keyed off each
 * row's top finding only — the same value that drives the Findings grouping — so the filter and
 * the grouping agree (a field belongs to exactly one finding bucket).
 */
function collectEntities(rows) {
  const set = new Set();
  rows.forEach((r) => {
    if (r.topEntity) set.add(r.topEntity);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Number of distinct entities in a findings value (object keys / array length / 1 for scalar). */
function findingsCount(findings) {
  if (findings == null || findings === '') return 0;
  if (Array.isArray(findings)) return findings.length;
  if (typeof findings === 'object') return Object.keys(findings).length;
  return 1;
}

/** Count for the top entity, or null when findings carry no numeric counts (array/scalar shapes). */
function topFindingCount(findings, topEntity) {
  if (!topEntity) return null;
  if (findings && typeof findings === 'object' && !Array.isArray(findings)) {
    const v = findings[topEntity];
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  }
  return null;
}

/**
 * Findings cell: the design shows ONLY the top finding (highest-count entity) as a pill, plus a
 * muted "+N" chip when the field matched more entities. This intentionally differs from the
 * shared FindingsColumn (which renders every finding) — that one stays in use by the mask dialog
 * and encryption table.
 */
function TopFindingCell({ findings, topEntity }) {
  const count = findingsCount(findings);
  if (count === 0) return <span className='inv-findings-none'>none</span>;
  const c = topFindingCount(findings, topEntity);
  return (
    <span className='d-inline-flex align-items-center'>
      <span className='inv-pill inv-pill-top'>{c != null ? `${topEntity} ${c}` : topEntity}</span>
      {count > 1 ? <span className='inv-pill ml-1'>{`+${count - 1}`}</span> : null}
    </span>
  );
}

function bucketKey(row, groupBy) {
  if (groupBy === 'schema') return row.schema || row.table;
  if (groupBy === 'entity') return row.topEntity || '(no findings)';
  if (groupBy === 'algorithm') return row.algorithm || '(no algorithm)';
  // table (default)
  return row.schema ? `${row.schema}.${row.table}` : row.table;
}

/** Sensitive (masking-candidate) columns in a bucket: rows with findings or a recommendation. */
function sensitiveCount(rows) {
  let n = 0;
  for (const r of rows || []) {
    if (hasFindingEntries(r.findings) || r.recommendedAlgorithm) n += 1;
  }
  return n;
}

/**
 * Order buckets by how many sensitive (masking-candidate) columns they contain, DESCENDING — so
 * the highest-PII tables surface first (critical at PeopleSoft scale: 1K+ tables, where the user
 * focuses on the few that matter rather than scrolling). Ties break alphabetically; "(no …)"
 * placeholder buckets always sort last. Exception: grouping by SCHEMA orders alphabetically —
 * a handful of schema groups is a navigation axis, not a triage list, and users expect A→Z.
 */
export function sortBuckets(keys, buckets, groupBy) {
  return keys.slice().sort((a, b) => {
    const pa = a.startsWith(NO_PREFIX);
    const pb = b.startsWith(NO_PREFIX);
    if (pa !== pb) return pa ? 1 : -1; // "(no …)" buckets last
    if (groupBy !== 'schema') {
      const sa = sensitiveCount(buckets[a]);
      const sb = sensitiveCount(buckets[b]);
      if (sa !== sb) return sb - sa; // more sensitive columns first
    }
    return a.localeCompare(b);
  });
}

function GroupLabel({ groupBy, bucket }) {
  if (groupBy === 'file') {
    // File-source view: the bucket is the file name verbatim — never splitTableKey it
    // (that would show "customers.csv" as table "csv" in schema "customers").
    return <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{bucket}</span>;
  }
  if (groupBy === 'table') {
    const { schema, table } = splitTableKey(bucket);
    return (
      <span>
        <span style={{ fontWeight: 500 }}>{table}</span>
        {schema ? <span className='text-muted ml-2 small'>{schema}</span> : null}
      </span>
    );
  }
  if (groupBy === 'schema') {
    return (
      <span style={{ fontWeight: 500 }}>
        <BsFolder className='mr-2' />
        {bucket}
      </span>
    );
  }
  if (groupBy === 'entity') {
    return (
      <span style={{ fontWeight: 500 }}>
        <BsShieldLock className='mr-2' />
        {bucket}
      </span>
    );
  }
  return <span style={{ fontWeight: 500 }}>{bucket}</span>;
}

const STATUS_TAG_CLASS = { new: 'inv-tag', differs: 'inv-tag inv-tag-warn' };

/** The recommendation cell + per-field apply (lifted from the Part A table renderer). */
function RecommendedCell({ row, onApply, readOnly }) {
  const { recommendedAlgorithm: rec, algorithm: cur } = row;
  // Nothing recommended and nothing set — there is no sync state to show.
  if (!rec && !cur) return <span className='inv-findings-none'>—</span>;
  if (rec === cur) {
    return (
      <span className='inv-sync'>
        <BsCheck className='inv-check' /> in sync
      </span>
    );
  }
  // A set algorithm whose recommendation is now empty (latest discover found nothing) is out of
  // sync like any other mismatch — applying the "none" recommendation clears the algorithm.
  const label = rec || 'none';
  if (readOnly) return <span className='inv-use' style={{ cursor: 'default' }}>{label}</span>;
  return (
    <button type='button' className='inv-use' title='Use recommended' onClick={onApply}>
      {label} <BsArrowReturnLeft className='ml-1' />
    </button>
  );
}

function AlgorithmSelect({ row, algorithms, onChange, readOnly }) {
  const ids = useMemo(
    () => new Set((algorithms || []).map((a) => a && a.id).filter(Boolean)),
    [algorithms]
  );
  const value = row.algorithm || '';
  const showUnknown = Boolean(value && !ids.has(value));
  // Uniqueness advisory (C2D-1129) — a non-unique algorithm on a key column fails at run
  // time with a duplicate-key error; surface it at assignment time. Constraint-aware when
  // the Profile recorded the column's PK/UNIQUE membership, name-heuristic otherwise. The
  // icon looks the same at every level; the level rides on data-level for the banner/tests.
  const warning = uniquenessWarning(row.tableKey, row.field, value, { keyConstraints: row.keyConstraints });
  return (
    <span className='d-inline-flex align-items-center' style={{ maxWidth: '100%' }}>
      <select
        className='inv-select'
        value={value}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value=''>— none —</option>
        {showUnknown ? <option value={value}>{value}</option> : null}
        {(algorithms || []).map((a) => (
          <option key={a.id || a.label} value={a.id} title={a.description || undefined}>
            {a.label || a.id}
            {a.id === row.recommendedAlgorithm ? ' ✓rec' : ''}
          </option>
        ))}
      </select>
      {warning && (
        <BsExclamationTriangleFill
          className='ml-1 flex-shrink-0 text-warning'
          title={warning.message}
          aria-label={warning.message}
          data-level={warning.level}
          data-testid={`uniqueness-warning-${row.tableKey}-${row.field}`}
        />
      )}
    </span>
  );
}

/** Pill switch ported from the mock — a styled <button aria-pressed>, not a bootstrap Form.Check. */
function MaskSwitch({ id, checked, disabled, onClick, title }) {
  return (
    <button
      id={id}
      type='button'
      className='inv-sw'
      aria-pressed={checked ? 'true' : 'false'}
      disabled={disabled}
      title={title}
      onClick={disabled ? undefined : onClick}
    />
  );
}

const COLS_MASK = '1fr 130px 150px 150px 56px';
const COLS_ENC = '1fr 150px 56px';

/** One field row. Grid columns mirror the design mock: field | findings | recommended | algorithm | mask. */
function FieldRow({ row, isEncryption, algorithms, readOnly, groupBy, onToggle, onAlgo, onApply, style }) {
  // 'file' buckets are one file per group — the file name is already the header.
  const showQualified = groupBy !== 'table' && groupBy !== 'file';
  // "Not on target": the selected mask target lacks this column / table, so the row is inert.
  const missing = Boolean(row.targetMissing);
  const locked = readOnly || missing;
  return (
    <div
      className={`inv-row${missing ? ' inv-row-target-missing' : ''}`}
      data-target-missing={missing ? row.targetMissingReason || 'column' : undefined}
      style={{
        ...(missing ? { opacity: 0.55 } : {}),
        display: 'grid',
        gridTemplateColumns: isEncryption ? COLS_ENC : COLS_MASK,
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        ...style,
      }}
    >
      <div className='text-truncate'>
        {showQualified ? (
          <span className='inv-fqual'>{`${row.schema ? row.schema + '.' : ''}${row.table}.`}</span>
        ) : null}
        <span className='inv-fname'>{row.field}</span>
        {/* status (new/differs) is recommendation-driven — masking only. Encryption has no
            per-field algorithm/recommendation, so no status badge there. */}
        {!isEncryption && !missing && row.status ? (
          <span className={`${STATUS_TAG_CLASS[row.status] || 'inv-tag'} ml-2`}>{row.status}</span>
        ) : null}
        {missing ? (
          <span
            className='badge badge-secondary ml-2'
            title={
              row.targetMissingReason === 'table'
                ? 'This table does not exist on the selected mask target'
                : 'This column does not exist on the selected mask target'
            }
            data-testid={`target-missing-${row.tableKey}-${row.field}`}
          >
            not on target
          </span>
        ) : null}
      </div>
      <div className='d-flex flex-wrap align-items-center'>
        <TopFindingCell findings={row.findings} topEntity={row.topEntity} />
      </div>
      {!isEncryption && (
        <div className='text-truncate'>
          <RecommendedCell row={row} onApply={onApply} readOnly={locked} />
        </div>
      )}
      {!isEncryption && (
        <div>
          <AlgorithmSelect row={row} algorithms={algorithms} onChange={onAlgo} readOnly={locked} />
        </div>
      )}
      <div className='text-center'>
        <MaskSwitch
          id={`${row.tableKey}-${row.field}-mask`}
          checked={row.checked}
          disabled={locked}
          onClick={onToggle}
        />
      </div>
    </div>
  );
}

function InventoryFieldsAccordion({
  selectedFields,
  setSelectedFields,
  algorithms = [],
  readOnly = false,
  isEncryption = false,
  /** Optional: lets the host show the existing "N fields being filtered" line. */
  onFilteredCountChange,
  /**
   * Optional server-search fallback for capped table-index loads (PeopleSoft-80K scale):
   * `{ totalTables, onSearch: async (query) => void }`. When set, a non-empty query also
   * runs server-side (debounced); the host's onSearch merges any missing tables into
   * selectedFields, and the new rows show up through the normal re-render. Hosts MUST
   * memoize this object — a fresh identity every render would re-arm the debounce loop.
   */
  serverSearch = null,
  /** Item/container nouns for the source type. Defaults to relational (table/schema). */
  nomenclature = { item: 'tables', itemSingular: 'table', container: 'database', containerSchema: 'schema' },
}) {
  const [query, setQuery] = useState('');
  const [identifiedOnly, setIdentifiedOnly] = useState(true);
  const [maskedOnly, setMaskedOnly] = useState(false);
  const [statusFilters, setStatusFilters] = useState({ new: false, differs: false });
  const [entityFilter, setEntityFilter] = useState('');
  const [groupBy, setGroupBy] = useState('table');
  const [collapsed, setCollapsed] = useState({}); // bucket -> true when explicitly collapsed
  const [expandedOverflow, setExpandedOverflow] = useState({}); // bucket -> render all (past CAP)

  const allRows = useMemo(() => flattenRows(selectedFields), [selectedFields]);
  const entities = useMemo(() => collectEntities(allRows), [allRows]);

  const rowPasses = useMemo(() => {
    const q = query.trim().toLowerCase();
    const anyStatus = statusFilters.new || statusFilters.differs;
    return (r) => {
      if (q && !(r.field.toLowerCase().includes(q) || r.table.toLowerCase().includes(q) || r.schema.toLowerCase().includes(q))) {
        return false;
      }
      if (identifiedOnly && !(hasFindingEntries(r.findings) || r.algorithm)) return false;
      if (maskedOnly && !r.checked) return false;
      if (anyStatus) {
        const ok = (statusFilters.new && r.status === 'new') || (statusFilters.differs && r.status === 'differs');
        if (!ok) return false;
      }
      if (entityFilter && r.topEntity !== entityFilter) return false;
      return true;
    };
  }, [query, identifiedOnly, maskedOnly, statusFilters, entityFilter]);

  const matched = useMemo(() => allRows.filter(rowPasses), [allRows, rowPasses]);

  // Report filtered count to the host (FilteredFieldsText line) without setState-in-render.
  useEffect(() => {
    if (onFilteredCountChange) onFilteredCountChange(matched.length);
  }, [matched.length, onFilteredCountChange]);

  const buckets = useMemo(() => {
    const m = {};
    matched.forEach((r) => {
      const k = bucketKey(r, groupBy);
      (m[k] = m[k] || []).push(r);
    });
    return m;
  }, [matched, groupBy]);

  const bucketKeys = useMemo(() => sortBuckets(Object.keys(buckets), buckets, groupBy), [buckets, groupBy]);
  const searching = query.trim() !== '';

  // ---- server-search fallback (capped index-mode inventories) ----
  // Debounced: every settled query also runs against the WHOLE run server-side; the host
  // merges missing tables into selectedFields. Local matches are not proof of completeness
  // (the cap may have dropped other matching tables), so this fires even when local rows match.
  const [serverStatus, setServerStatus] = useState('idle'); // idle | searching | done | error
  const serverSeqRef = useRef(0);
  useEffect(() => {
    if (!serverSearch || typeof serverSearch.onSearch !== 'function') return undefined;
    const q = query.trim();
    const seq = (serverSeqRef.current += 1);
    if (q.length < 2) {
      setServerStatus('idle');
      return undefined;
    }
    setServerStatus('searching');
    const timer = setTimeout(async () => {
      try {
        await serverSearch.onSearch(q);
        if (seq === serverSeqRef.current) setServerStatus('done');
      } catch (e) {
        if (seq === serverSeqRef.current) setServerStatus('error');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, serverSearch]);

  // Fields that match the query but are hidden by the other filters (e.g. the Recommended
  // chip hiding no-findings fields) — surfaced in the empty state so a successful server
  // fetch doesn't read as "no results".
  const queryOnlyMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return 0;
    return allRows.filter(
      (r) =>
        r.field.toLowerCase().includes(q) ||
        r.table.toLowerCase().includes(q) ||
        r.schema.toLowerCase().includes(q)
    ).length;
  }, [allRows, query]);

  // On first show (and whenever the grouping axis changes), expand the FIRST group and collapse
  // the rest. Re-armed on groupBy change; doesn't fight the user's later manual toggles.
  const initedForGroupByRef = useRef(null);
  useEffect(() => {
    if (initedForGroupByRef.current === groupBy) return;
    if (bucketKeys.length === 0) return; // wait until buckets exist
    initedForGroupByRef.current = groupBy;
    const next = {};
    bucketKeys.forEach((k, i) => {
      if (i > 0) next[k] = true; // collapse all but the first
    });
    setCollapsed(next);
  }, [groupBy, bucketKeys]);

  // ---- writers: every mutation builds a fresh selectedFields and calls setSelectedFields once.
  const writeField = (tableKey, field, patch) => {
    setSelectedFields((prev) => {
      const t = prev[tableKey];
      if (!t || !t[field]) return prev;
      return { ...prev, [tableKey]: { ...t, [field]: { ...t[field], ...patch } } };
    });
  };

  // Bulk over a set of rows: one new object, one setState. `compute(row)` returns the patch.
  const writeRows = (rows, compute) => {
    setSelectedFields((prev) => {
      const next = { ...prev };
      const touchedTables = {};
      rows.forEach((r) => {
        const t = touchedTables[r.tableKey] || next[r.tableKey];
        if (!t || !t[r.field]) return;
        const patch = compute(r);
        if (!patch) return;
        const newTable = touchedTables[r.tableKey] ? t : { ...t };
        newTable[r.field] = { ...newTable[r.field], ...patch };
        touchedTables[r.tableKey] = newTable;
      });
      Object.keys(touchedTables).forEach((tk) => {
        next[tk] = touchedTables[tk];
      });
      return next;
    });
  };

  const toggleGroupMask = (rows) => {
    if (readOnly) return;
    const allOn = rows.length > 0 && rows.every((r) => r.checked);
    writeRows(rows, () => ({ checked: !allOn }));
  };

  const setAllInGroup = (rows, value) => {
    if (readOnly || !value) return;
    if (value === '__REC__') {
      writeRows(rows, (r) => ({
        algorithm: r.recommendedAlgorithm || '',
        params: r.recommendedParams || {},
      }));
    } else {
      writeRows(rows, () => ({ algorithm: value }));
    }
  };

  const toggleCollapsed = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));
  const toggleStatus = (key) => setStatusFilters((s) => ({ ...s, [key]: !s[key] }));
  const collapseAll = () => {
    const next = {};
    bucketKeys.forEach((k) => { next[k] = true; });
    setCollapsed(next);
  };
  const expandAll = () => setCollapsed({});

  // A group is open when searching (search force-opens matches) or not explicitly collapsed.
  const isOpen = (k) => searching || !collapsed[k];

  // Window the GROUP list itself once there are many tables (PeopleSoft scale). Below the
  // threshold, render groups directly so a single wide table keeps its inner row-virtualization.
  const windowed = bucketKeys.length > GROUP_WINDOW_THRESHOLD;

  // Renders one accordion group with all handlers wired. `opts.windowedRows` forces capped
  // (non-virtualized) rows so the group's height is deterministic inside the windowed list.
  const renderGroup = (k, opts = {}) => (
    <GroupAccordion
      key={k}
      bucketKey={k}
      list={buckets[k]}
      open={isOpen(k)}
      groupBy={groupBy}
      isEncryption={isEncryption}
      algorithms={algorithms}
      readOnly={readOnly}
      searching={searching}
      windowedRows={Boolean(opts.windowedRows)}
      showAll={Boolean(expandedOverflow[k])}
      onShowAll={() => setExpandedOverflow((o) => ({ ...o, [k]: true }))}
      onToggleCollapsed={toggleCollapsed}
      onToggleGroupMask={toggleGroupMask}
      onSetAllInGroup={setAllInGroup}
      onToggleRow={(r) => writeField(r.tableKey, r.field, { checked: !r.checked })}
      onAlgoRow={(r, v) => writeField(r.tableKey, r.field, { algorithm: v })}
      onApplyRow={(r) =>
        writeField(r.tableKey, r.field, {
          algorithm: r.recommendedAlgorithm || '',
          params: r.recommendedParams || {},
        })
      }
    />
  );

  // 'entity' axis is labelled "Findings" (the word used in the table) — it buckets by each row's
  // top finding. Internal id stays 'entity'. The 'table'/'schema' axis LABELS follow the source
  // type (Table/Schema for relational, Collection/Database for mongo, File/Folder for file); the
  // internal axis ids stay 'table'/'schema'.
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const itemLabel = cap(nomenclature.itemSingular);
  const containerLabel = cap(nomenclature.containerSchema || nomenclature.container);
  const groupByOptions = isEncryption
    ? [['table', itemLabel], ['schema', containerLabel], ['entity', 'Findings']]
    : [['table', itemLabel], ['schema', containerLabel], ['entity', 'Findings'], ['algorithm', 'Algorithm']];

  return (
    <div className='inventory-accordion'>
      {/* filters — sticky to the top of the page's scroll body */}
      <div className='inv-filters'>
      <div style={{ position: 'relative' }} className='mb-2'>
        <BsSearch
          aria-hidden
          style={{ position: 'absolute', left: 10, top: 10, color: 'var(--inv-text-tertiary)' }}
        />
        <input
          data-testid='search-mask-inventory-fields'
          type='text'
          className='inv-search'
          style={{ paddingLeft: 30 }}
          placeholder={`Search field, ${nomenclature.itemSingular} or ${nomenclature.containerSchema || nomenclature.container}…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {serverSearch && searching ? (
          <div
            data-testid='inv-server-search-status'
            style={{ fontSize: 12, color: 'var(--inv-text-secondary)', marginTop: 4 }}
          >
            {serverStatus === 'searching'
              ? `Searching all ${Number(serverSearch.totalTables).toLocaleString()} ${nomenclature.item}…`
              : serverStatus === 'error'
                ? `Full-source search failed — results cover the loaded ${nomenclature.item} only.`
                : `${itemLabel}-name matches from all ${Number(serverSearch.totalTables).toLocaleString()} ${nomenclature.item} are included.`}
          </div>
        ) : null}
      </div>
      <div className='d-flex flex-wrap align-items-center mb-2' style={{ gap: 6 }}>
        <button
          type='button'
          className={`inv-chip${identifiedOnly ? ' on' : ''}`}
          onClick={() => setIdentifiedOnly((v) => !v)}
        >
          Recommended
        </button>
        <button
          type='button'
          className={`inv-chip${maskedOnly ? ' on' : ''}`}
          onClick={() => setMaskedOnly((v) => !v)}
        >
          {isEncryption ? 'Selected to encrypt' : 'Selected to mask'}
        </button>
        {/* status chips are recommendation-driven — masking only (see status badge note). */}
        {!isEncryption && (
          <>
            <span className='inv-sep' />
            <button
              type='button'
              className={`inv-chip${statusFilters.new ? ' on' : ''}`}
              onClick={() => toggleStatus('new')}
            >
              New
            </button>
            <button
              type='button'
              className={`inv-chip${statusFilters.differs ? ' on' : ''}`}
              onClick={() => toggleStatus('differs')}
            >
              Differs from recommended
            </button>
          </>
        )}
        {entities.length > 0 && (
          <select
            className='inv-setall'
            style={{ width: 'auto' }}
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            title='Filter to fields whose primary finding is this'
          >
            <option value=''>All findings</option>
            {entities.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        )}
        {/* filtered count, pushed to the far right of the filter row */}
        <span style={{ flex: 1 }} />
        {matched.length < allRows.length ? (
          <span className='inv-filtered-count'>
            {matched.length.toLocaleString()} of {allRows.length.toLocaleString()} fields shown
          </span>
        ) : null}
      </div>

      {/* group by */}
      <div className='d-flex align-items-center mb-3' style={{ gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--inv-text-secondary)' }}>Group by</span>
        <div className='inv-gbgroup'>
          {groupByOptions.map(([g, label]) => (
            <button
              key={g}
              type='button'
              className={`inv-gb${groupBy === g ? ' on' : ''}`}
              onClick={() => {
                setGroupBy(g);
                // Re-arm the first-open-rest-collapsed default for the new axis.
                initedForGroupByRef.current = null;
                setExpandedOverflow({});
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <button type='button' className='inv-chip' onClick={collapseAll}>Collapse all</button>
        <button type='button' className='inv-chip' onClick={expandAll}>Expand all</button>
      </div>
      </div>{/* /.inv-filters */}

      {bucketKeys.length === 0 ? (
        <div className='inv-empty'>
          {serverSearch && serverStatus === 'searching'
            ? `Searching all ${Number(serverSearch.totalTables).toLocaleString()} tables…`
            : queryOnlyMatches > 0
              ? `${queryOnlyMatches.toLocaleString()} matching field${queryOnlyMatches === 1 ? ' is' : 's are'} hidden by the active filter chips — turn them off to see ${queryOnlyMatches === 1 ? 'it' : 'them'}.`
              : 'No fields match the current filters.'}
        </div>
      ) : windowed ? (
        <WindowedGroupList
          bucketKeys={bucketKeys}
          buckets={buckets}
          isOpen={isOpen}
          renderGroup={renderGroup}
        />
      ) : (
        bucketKeys.map((k) => renderGroup(k))
      )}
    </div>
  );
}

/**
 * One accordion group (header + rows). Extracted so it can render both directly and as a row
 * inside the virtualized group list. `windowedRows` forces the rows into a capped (non-virtualized)
 * render so the group's height is deterministic — required when the GROUP list is itself windowed
 * (we don't nest two react-window lists).
 *
 * Exported for the file-source inventory view (FileInventoryGroups), which renders one
 * group per file and injects a per-file Mask button via `headerExtra`.
 */
export function GroupAccordion({
  bucketKey: k,
  list,
  open,
  groupBy,
  isEncryption,
  algorithms,
  readOnly,
  showAll,
  onShowAll,
  onToggleCollapsed,
  onToggleGroupMask,
  onSetAllInGroup,
  onToggleRow,
  onAlgoRow,
  onApplyRow,
  searching,
  windowedRows = false,
  /** Optional node rendered in the header between the Set-all select and the group mask switch. */
  headerExtra,
  style,
}) {
  const nSel = list.filter((r) => r.checked).length;
  const allOn = list.length > 0 && list.every((r) => r.checked);
  return (
    <div className='inv-acc' style={style}>
      <div className='inv-head' onClick={() => !searching && onToggleCollapsed(k)}>
        {open ? <BsChevronDown className='inv-chevron' /> : <BsChevronRight className='inv-chevron' />}
        <div className='flex-grow-1 text-truncate'>
          <GroupLabel groupBy={groupBy} bucket={k} />
        </div>
        <span className='inv-mini mr-2'>
          {list.length.toLocaleString()} fields · {nSel} {isEncryption ? 'encrypted' : 'masked'}
        </span>
        {headerExtra}
        {!isEncryption && (
          <select
            className='inv-setall'
            value=''
            disabled={readOnly}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              onSetAllInGroup(list, e.target.value);
              e.target.value = '';
            }}
          >
            <option value=''>Set all →</option>
            <option value='__REC__'>Use recommended</option>
            {(algorithms || []).map((a) => (
              <option key={a.id || a.label} value={a.id}>
                {a.label || a.id}
              </option>
            ))}
          </select>
        )}
        {/* "mask whole group" toggle on the FAR RIGHT, aligned with the per-field mask
            column (56px, centered) so all mask toggles line up on the right edge. */}
        <span className='inv-group-mask'>
          <MaskSwitch
            id={`group-${k}-mask`}
            checked={allOn}
            disabled={readOnly}
            title='Mask all shown'
            onClick={(e) => {
              e.stopPropagation();
              onToggleGroupMask(list);
            }}
          />
        </span>
      </div>

      {open && (
        <GroupRows
          list={list}
          isEncryption={isEncryption}
          algorithms={algorithms}
          readOnly={readOnly}
          groupBy={groupBy}
          showAll={showAll}
          onShowAll={onShowAll}
          noVirtualize={windowedRows}
          onToggle={onToggleRow}
          onAlgo={onAlgoRow}
          onApply={onApplyRow}
        />
      )}
    </div>
  );
}

/**
 * Virtualized list of accordion GROUPS for many-table databases. Only on-screen table headers
 * mount, so a 1K-10K-table source stays responsive. Heights are computed (collapsed = header;
 * open = header + col-header + capped rows + optional tip). Expanded groups render rows capped
 * (no inner react-window) so each group's height is deterministic for the outer list.
 */
function WindowedGroupList({ bucketKeys, buckets, isOpen, renderGroup }) {
  const listRef = useRef(null);

  const itemSize = (index) => {
    const k = bucketKeys[index];
    const list = buckets[k] || [];
    if (!isOpen(k)) return GROUP_HEADER_H + GROUP_ROW_GAP;
    const shown = Math.min(list.length, CAP);
    const capped = list.length > CAP;
    return (
      GROUP_HEADER_H +
      GROUP_COLHEADER_H +
      shown * VIRTUAL_ROW_HEIGHT +
      (capped ? GROUP_TIP_H : 0) +
      GROUP_ROW_GAP
    );
  };

  // Recompute heights whenever which groups are open / the data shape changes.
  const openKey = bucketKeys.map((k) => (isOpen(k) ? '1' : '0')).join('') + ':' + bucketKeys.length;
  useEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
  }, [openKey]);

  const height = Math.min(
    GROUP_LIST_MAX_H,
    bucketKeys.reduce((sum, _k, i) => sum + itemSize(i), 0)
  );

  return (
    <VariableSizeList
      ref={listRef}
      height={height}
      width='100%'
      itemCount={bucketKeys.length}
      itemSize={itemSize}
      estimatedItemSize={GROUP_HEADER_H + GROUP_ROW_GAP}
    >
      {({ index, style }) => {
        // react-window positions each item absolutely; render the group capped (windowedRows) so
        // its measured height matches itemSize. The bottom gap is folded into itemSize.
        const inner = renderGroup(bucketKeys[index], { windowedRows: true });
        return <div style={style}>{inner}</div>;
      }}
    </VariableSizeList>
  );
}

/**
 * Rows for one open group. Regimes:
 *  - small group → plain DOM rows
 *  - large group, capped → first CAP rows + a "showing N of M" expander
 *  - large group, user expanded → react-window virtualized list
 *  - `noVirtualize` (group list is itself windowed) → always capped, never an inner virtual list
 *    and no "show all" (so the group's height stays deterministic for the outer VariableSizeList).
 */
function GroupRows({
  list,
  isEncryption,
  algorithms,
  readOnly,
  groupBy,
  showAll,
  onShowAll,
  noVirtualize = false,
  onToggle,
  onAlgo,
  onApply,
}) {
  const colHeaderStyle = {
    display: 'grid',
    gridTemplateColumns: isEncryption ? COLS_ENC : COLS_MASK,
    gap: 10,
    padding: '6px 12px',
  };
  const header = (
    <div className='inv-colh' style={colHeaderStyle}>
      <div>Field</div>
      <div>Findings</div>
      {!isEncryption && <div>Recommended</div>}
      {!isEncryption && <div>Algorithm</div>}
      <div className='text-center'>{isEncryption ? 'Encrypt' : 'Mask'}</div>
    </div>
  );

  const renderRow = (r, style) => (
    <FieldRow
      key={`${r.tableKey}-${r.field}`}
      row={r}
      isEncryption={isEncryption}
      algorithms={algorithms}
      readOnly={readOnly}
      groupBy={groupBy}
      onToggle={() => onToggle(r)}
      onAlgo={(v) => onAlgo(r, v)}
      onApply={() => onApply(r)}
      style={style}
    />
  );

  // Inner row-virtualization: only when NOT inside a windowed group list (no nesting), and the user
  // expanded a genuinely large group.
  if (!noVirtualize && showAll && list.length > GROUP_VIRTUALIZE_THRESHOLD) {
    const height = Math.min(VIRTUAL_LIST_MAX_HEIGHT, list.length * VIRTUAL_ROW_HEIGHT);
    return (
      <div>
        {header}
        <FixedSizeList height={height} itemCount={list.length} itemSize={VIRTUAL_ROW_HEIGHT} width='100%'>
          {({ index, style }) => renderRow(list[index], style)}
        </FixedSizeList>
      </div>
    );
  }

  // Capped render. In windowed mode (`noVirtualize`) we always cap and omit "show all" so the
  // height matches the outer list's itemSize; the user narrows via search instead.
  const capped = (noVirtualize || !showAll) && list.length > CAP;
  const visible = capped ? list.slice(0, CAP) : list;
  return (
    <div>
      {header}
      {visible.map((r) => renderRow(r))}
      {capped && !noVirtualize && (
        <button type='button' className='inv-tip' onClick={onShowAll}>
          Showing {CAP.toLocaleString()} of {list.length.toLocaleString()} — show all
        </button>
      )}
      {capped && noVirtualize && (
        <div className='inv-tip'>
          Showing {CAP.toLocaleString()} of {list.length.toLocaleString()} — search to narrow
        </div>
      )}
    </div>
  );
}

export default memo(InventoryFieldsAccordion);
